import { AppState, Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import { toArray } from "@gromia/utils/fp";
import * as A from "fp-ts/lib/Array";
import { pipe } from "fp-ts/lib/function";
import * as O from "fp-ts/lib/Option";
import * as TE from "fp-ts/lib/TaskEither";
import { navigate } from "gatsby";
import * as t from "io-ts";
import * as React from "react";

export { withAuthenticationRequired } from "@auth0/auth0-react";

function onRedirectCallback(appState?: AppState) {
  console.log("appState", appState);
  navigate(appState?.returnTo || "/", { replace: true });
}

export function AuthProvider(props: {
  children: React.ReactNode;
  redirectUri: string;
}) {
  return (
    <Auth0Provider
      domain={process.env.AUTH0_DOMAIN as string}
      clientId={process.env.AUTH0_CLIENT_ID as string}
      audience={process.env.AUTH0_AUDIENCE as string}
      useRefreshTokens={true}
      cacheLocation="localstorage"
      onRedirectCallback={onRedirectCallback}
      redirectUri={props.redirectUri}
    >
      {props.children}
    </Auth0Provider>
  );
}

export type UserData = {
  email: string;
  name?: string;
  token: string;
  picture?: string;
};

type AuthData = {
  logout: () => void;
  token: TE.TaskEither<unknown, { Authorization: string }>;
  loginWithRedirect: () => void;
  user: UserData;
};

export function useAuth(): AuthData {
  const {
    getAccessTokenSilently,
    getAccessTokenWithPopup,
    user,
    logout,
    loginWithRedirect,
  } = useAuth0();

  function getAccessTokenSilently_() {
    return getAccessTokenSilently();
  }

  function getAccessTokenWithPopup_() {
    return getAccessTokenWithPopup();
  }

  function parseGetAccessTokenError(e: unknown): string {
    const Auth0Error = t.type({ error: t.string });
    const result = pipe(
      Auth0Error.decode(e),
      O.fromEither,
      O.map(x => {
        return x.error;
      }),
    );

    if (O.isNone(result)) {
      console.error(e);
    }

    return pipe(
      result,
      O.getOrElse(() => "unknown_error"),
    );
  }

  function getApiHeaders() {
    return pipe(
      TE.tryCatch(getAccessTokenSilently_, parseGetAccessTokenError),
      TE.orElse(e => {
        if (e === "consent_required") {
          return TE.tryCatch(
            getAccessTokenWithPopup_,
            parseGetAccessTokenError,
          );
        }
        return TE.left(e);
      }),
      TE.mapLeft(x => {
        console.log(`token error`, x);
        return x;
      }),
      TE.map(x => {
        console.log(`token`, x);
        return x;
      }),
      TE.map(x => ({
        Authorization: `Bearer ${x}`,
      })),
    );
  }

  return {
    user: user as any,
    logout: () => logout({ returnTo: process.env.DEFAULT_HOST }),
    loginWithRedirect: () => loginWithRedirect({ ui_locales: "it" }),
    token: getApiHeaders(),
  };
}

type Role = "admin" | "agent" | "user" | "referrer" | "homeowner";

export function foldUserL<T>(
  ifUser: () => T,
  ifHomeOwner: () => T,
  ifReferrer: () => T,
  ifAgent: () => T,
  ifAdministrator: () => T,
) {
  return (user: AuthData["user"]) => {
    const ns = process.env.AUTH0_FIELD_NAMESPACE as string;
    const r: Role[] = (user as any)[ns + "roles"] || [];

    const priority = {
      admin: ifAdministrator,
      agent: ifAgent,
      homeowner: ifHomeOwner,
      referrer: ifReferrer,
      user: ifUser,
    };

    const fn = pipe(
      priority,
      toArray,
      A.filter(x => r.includes(x[0])),
      A.map(x => x[1]),
      A.head,
      O.getOrElse(() => ifUser),
    );

    return fn();
  };
}

export const isAdministrator = foldUserL(
  () => false,
  () => false,
  () => false,
  () => false,
  () => true,
);

export function getIfAdministratorL<T>(ifAdministrator: () => T) {
  return foldUserL(
    () => null,
    () => null,
    () => null,
    () => null,
    ifAdministrator,
  );
}

export const getPriorityRole: (x: AuthData["user"]) => Role = foldUserL(
  () => "user",
  () => "homeowner",
  () => "referrer",
  () => "agent",
  () => "admin",
);

export function getUserFunnel(user: AuthData["user"]): O.Option<string> {
  const ns = process.env.AUTH0_FIELD_NAMESPACE as string;
  const r: string = (user as any)[ns + "funnel"];
  return O.fromNullable(r);
}

export function useUserFunnel() {
  const { user } = useAuth();
  return getUserFunnel(user);
}
